import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, RotateCcw, X, ZoomIn } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Link, useSearchParams } from "react-router-dom";

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";
const STATUS_COLORS = { 未確認: "text-amber-400", 承認: "text-green-400", 差戻し: "text-red-400" };
const CATEGORIES = ["施工前", "施工中", "施工後"];

export default function AdminSitePhotos() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState(params.get("project_id") || "");
  const [filterStatus, setFilterStatus] = useState("");
  const [viewing, setViewing] = useState(null);

  const load = () => {
    const params = {};
    if (filterProject) params.project_id = filterProject;
    if (filterStatus) params.status = filterStatus;
    api.get("/admin/site-photos", { params }).then((r) => setItems(r.data)).catch(() => toast.error("取得に失敗しました"));
  };

  useEffect(() => {
    api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterProject, filterStatus]);

  const review = async (p, status) => {
    try {
      await api.put(`/admin/site-photos/${p.id}`, { status });
      toast.success(status === "承認" ? "承認しました" : "差戻しました");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const changeCategory = async (p, category) => {
    try {
      await api.put(`/admin/site-photos/${p.id}`, { category });
      load();
    } catch {
      toast.error("変更に失敗しました");
    }
  };

  const imgUrl = (id) => `${api.defaults.baseURL}/site-photos/${id}/image`;

  return (
    <div data-testid="admin-site-photos-page">
      {params.get("project_id") && <Link data-testid="photos-back-workflow" to={`/admin/workflow/${params.get("project_id")}?tab=site`} className="mb-4 inline-block text-sm text-accent">← 案件の現場管理へ戻る</Link>}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-mincho text-2xl font-bold text-white">現場写真の確認</h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">SITE PHOTOS — スタッフ登録写真の確認・承認・差戻し</p>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <select data-testid="photos-filter-project" value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="min-w-0 max-w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none focus:border-accent">
            <option value="">すべての案件</option>
            {projects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
          </select>
          <select data-testid="photos-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none focus:border-accent">
            <option value="">すべての状態</option>
            <option>未確認</option><option>承認</option><option>差戻し</option>
          </select>
        </div>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {items.map((p) => (
          <div key={p.id} data-testid={`photo-card-${p.id}`} className="border border-line bg-panel">
            <button data-testid={`photo-view-${p.id}`} onClick={() => setViewing(p)} className="group relative block w-full">
              <img src={imgUrl(p.id)} alt={p.title} className="aspect-[4/3] w-full object-cover" />
              <span className="absolute inset-0 flex items-center justify-center bg-ink/0 transition-colors group-hover:bg-ink/40">
                <ZoomIn className="h-6 w-6 text-white opacity-0 transition-opacity group-hover:opacity-100" />
              </span>
            </button>
            <div className="space-y-2 p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-xs font-bold text-white">{p.title || p.category}</p>
                <span data-testid={`photo-status-${p.id}`} className={`shrink-0 text-[10px] font-bold ${STATUS_COLORS[p.status]}`}>{p.status}</span>
              </div>
              <p className="text-[10px] text-gray-500">{p.project_name} ・ {p.staff_name}</p>
              {p.description && (
                <p className="line-clamp-2 text-[10px] text-gray-400" title={p.description}>{p.description}</p>
              )}
              <div className="flex items-center gap-1.5 pt-1">
                <select data-testid={`photo-category-${p.id}`} value={p.category} onChange={(e) => changeCategory(p, e.target.value)} className="border border-line bg-ink px-2 py-1.5 text-[10px] text-gray-300 outline-none focus:border-accent">
                  {CATEGORIES.map((c) => (<option key={c}>{c}</option>))}
                </select>
                <button data-testid={`photo-approve-${p.id}`} onClick={() => review(p, "承認")} className="flex items-center gap-1 border border-green-500/40 px-2.5 py-1.5 text-[10px] font-bold text-green-400 transition-colors hover:bg-green-500/10">
                  <Check className="h-3 w-3" /> 承認
                </button>
                <button data-testid={`photo-reject-${p.id}`} onClick={() => review(p, "差戻し")} className="flex items-center gap-1 border border-red-500/40 px-2.5 py-1.5 text-[10px] font-bold text-red-400 transition-colors hover:bg-red-500/10">
                  <RotateCcw className="h-3 w-3" /> 差戻し
                </button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="col-span-full py-10 text-center text-sm text-gray-500">写真がまだ登録されていません。</p>}
      </div>

      {viewing && (
        <div data-testid="photo-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-ink/90 p-4 backdrop-blur-sm" onClick={() => setViewing(null)}>
          <div className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto border border-line bg-coal p-4" onClick={(e) => e.stopPropagation()}>
            <button data-testid="photo-modal-close" onClick={() => setViewing(null)} className="absolute right-3 top-3 z-10 bg-ink/70 p-1.5 text-gray-400 hover:text-white"><X className="h-5 w-5" /></button>
            <img src={imgUrl(viewing.id)} alt={viewing.title} className="max-h-[60vh] w-full object-contain" />
            <div className="mt-3 space-y-1 text-xs text-gray-400">
              <p className="text-sm font-bold text-white">{viewing.title || viewing.category}</p>
              <p>{viewing.project_name} ・ {viewing.staff_name} ・ {viewing.created_at?.replace("T", " ").slice(0, 16)}</p>
              {viewing.description && <p className="whitespace-pre-wrap">{viewing.description}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
